// Stage 7 — ⌘K Command Palette.
//
// A centered modal that surfaces every entry in the shortcut registry behind
// fuzzy search. Discoverability backstop for the 8-tab rail: a user who has
// never seen the Reductions tab can still find and open it through here.
//
// Mechanics:
//   - Opens on PALETTE_OPEN intent (the shortcut registry dispatches it on
//     ⌘K / Ctrl+K). Closes on PALETTE_CLOSE, Esc, backdrop click, or after
//     executing a command.
//   - While open, the palette's own keydown listener owns the keyboard:
//     ↑/↓ navigate (wrap), Enter executes, all other keys feed the input.
//   - Dynamic commands (per-selection focus, per-reduction focus) re-register
//     on `params.selectionOrder` / `params.reductionOrder` changes.
//
// State is module-local — no store schema entry is needed because nothing
// outside the palette needs to read open/query.

import { store, params } from '@store/instance';
import { INTENTS } from '@store/intents';
import { getShortcutRegistry, formatChord, type CommandDef } from '@input/shortcutRegistry';
import { scoreCommand } from './fuzzy';
import { isHelpOpen } from '../dom';
import type { WindowRegistry } from '../windowManager';
import type { ViewerContext } from '@app/context';

const MAX_VISIBLE = 20;

interface PaletteDom {
    readonly root: HTMLElement;
    readonly backdrop: HTMLElement;
    readonly input: HTMLInputElement;
    readonly list: HTMLElement;
    readonly emptyState: HTMLElement;
}

function buildDom(): PaletteDom {
    const root = document.createElement('div');
    root.id = 'palette-root';
    root.className = 'palette-root';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Command palette');
    root.hidden = true;

    const backdrop = document.createElement('div');
    backdrop.className = 'palette-backdrop';

    const card = document.createElement('div');
    card.className = 'palette-card';

    const inputWrap = document.createElement('div');
    inputWrap.className = 'palette-input-wrap';
    const inputIcon = document.createElement('i');
    inputIcon.className = 'fas fa-search palette-input-icon';
    inputIcon.setAttribute('aria-hidden', 'true');
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'palette-input';
    input.placeholder = 'Search commands…';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.setAttribute('aria-label', 'Command palette search');
    inputWrap.appendChild(inputIcon);
    inputWrap.appendChild(input);

    const list = document.createElement('ul');
    list.className = 'palette-list';
    list.setAttribute('role', 'listbox');

    const emptyState = document.createElement('div');
    emptyState.className = 'palette-empty';
    emptyState.textContent = 'No matching commands';
    emptyState.hidden = true;

    card.appendChild(inputWrap);
    card.appendChild(list);
    card.appendChild(emptyState);
    root.appendChild(backdrop);
    root.appendChild(card);

    document.body.appendChild(root);
    return { root, backdrop, input, list, emptyState };
}

interface RankedCommand {
    readonly def: CommandDef;
    readonly score: number;
}

function rankCommands(query: string, all: readonly CommandDef[]): RankedCommand[] {
    const trimmed = query.trim();
    const out: RankedCommand[] = [];
    for (const def of all) {
        if (trimmed) {
            const s = scoreCommand(trimmed, { label: def.label, ...(def.aliases ? { aliases: def.aliases } : {}), id: def.id });
            if (s === null) continue;
            out.push({ def, score: s });
        } else {
            // Empty query: surface every command, with a stable order — by
            // category, then by label, so the list reads like the help tab.
            out.push({ def, score: 0 });
        }
    }
    if (trimmed) {
        out.sort((a, b) => b.score - a.score);
    } else {
        out.sort((a, b) => {
            if (a.def.category !== b.def.category) return a.def.category.localeCompare(b.def.category);
            return a.def.label.localeCompare(b.def.label);
        });
    }
    return out.slice(0, MAX_VISIBLE);
}

export interface InstallPaletteOpts {
    readonly viewer: ViewerContext;
    readonly windowRegistry: WindowRegistry;
}

export function installPaletteDriver(opts: InstallPaletteOpts): () => void {
    const { viewer, windowRegistry } = opts;
    const registry = getShortcutRegistry();
    const dom = buildDom();

    let open = false;
    let query = '';
    let selectedIndex = 0;
    let visible: RankedCommand[] = [];
    let restoreFocus: HTMLElement | null = null;

    const setSelected = (i: number): void => {
        if (visible.length === 0) { selectedIndex = 0; return; }
        const n = visible.length;
        selectedIndex = ((i % n) + n) % n;
        const rows = dom.list.querySelectorAll<HTMLElement>('.palette-item');
        rows.forEach((row, idx) => {
            const isActive = idx === selectedIndex;
            row.setAttribute('aria-selected', isActive ? 'true' : 'false');
            if (isActive) row.classList.add('is-active');
            else row.classList.remove('is-active');
        });
        const active = rows[selectedIndex];
        if (active) active.scrollIntoView({ block: 'nearest' });
    };

    const render = (): void => {
        visible = rankCommands(query, registry.list());
        dom.list.textContent = '';
        if (visible.length === 0) {
            dom.emptyState.hidden = false;
            return;
        }
        dom.emptyState.hidden = true;

        for (let i = 0; i < visible.length; i++) {
            const { def } = visible[i]!;
            const row = document.createElement('li');
            row.className = 'palette-item';
            if (def.enabled === false) row.classList.add('is-disabled');
            row.setAttribute('role', 'option');
            row.setAttribute('data-index', String(i));

            const label = document.createElement('span');
            label.className = 'palette-item-label';
            label.textContent = def.label;

            const meta = document.createElement('span');
            meta.className = 'palette-item-meta';
            const cat = document.createElement('span');
            cat.className = 'palette-item-category';
            cat.textContent = def.category;
            meta.appendChild(cat);

            const keys = registry.keysFor(def.id);
            if (keys.length > 0) {
                const chord = document.createElement('span');
                chord.className = 'palette-item-keys';
                chord.textContent = formatChord(keys[0]!);
                meta.appendChild(chord);
            }

            row.appendChild(label);
            row.appendChild(meta);
            dom.list.appendChild(row);
        }
        setSelected(0);
    };

    const execAt = (i: number): void => {
        const entry = visible[i];
        if (!entry || entry.def.enabled === false) return;
        // Close before run() so a command that opens another modal lands in
        // a clean state. The exception is help.open / console.toggle whose
        // behavior is fine either way.
        closePalette();
        registry.invoke(entry.def.id);
    };

    const onKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
            e.preventDefault(); e.stopPropagation();
            closePalette();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault(); e.stopPropagation();
            setSelected(selectedIndex + 1);
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault(); e.stopPropagation();
            setSelected(selectedIndex - 1);
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault(); e.stopPropagation();
            execAt(selectedIndex);
            return;
        }
    };

    const onInput = (): void => {
        query = dom.input.value;
        render();
    };

    const onListClick = (e: MouseEvent): void => {
        const row = (e.target as HTMLElement).closest<HTMLElement>('.palette-item');
        if (!row) return;
        const idx = Number(row.getAttribute('data-index') ?? '-1');
        if (!Number.isFinite(idx) || idx < 0) return;
        execAt(idx);
    };

    const onBackdropClick = (): void => closePalette();

    const openPalette = (): void => {
        if (open) {
            // Re-open while open = focus + clear, a common cmd-palette pattern.
            dom.input.value = '';
            query = '';
            render();
            dom.input.focus();
            dom.input.select();
            return;
        }
        open = true;
        restoreFocus = (document.activeElement instanceof HTMLElement) ? document.activeElement : null;
        dom.root.hidden = false;
        dom.input.value = '';
        query = '';
        render();
        dom.input.addEventListener('input', onInput);
        dom.input.addEventListener('keydown', onKeyDown);
        dom.list.addEventListener('click', onListClick);
        dom.backdrop.addEventListener('click', onBackdropClick);
        requestAnimationFrame(() => dom.input.focus());
    };

    const closePalette = (): void => {
        if (!open) return;
        open = false;
        dom.root.hidden = true;
        dom.input.removeEventListener('input', onInput);
        dom.input.removeEventListener('keydown', onKeyDown);
        dom.list.removeEventListener('click', onListClick);
        dom.backdrop.removeEventListener('click', onBackdropClick);
        // Defer focus restore so the just-fired command can land first.
        requestAnimationFrame(() => {
            try { restoreFocus?.focus(); } catch { /* element gone */ }
            restoreFocus = null;
        });
    };

    // ─── command registration ──────────────────────────────────────────────

    const registrations: Array<() => void> = [];

    const reg = (def: CommandDef): void => { registrations.push(registry.register(def)); };

    // Palette itself.
    reg({
        id: 'palette.open',
        label: 'Open command palette',
        category: 'Palette',
        aliases: ['commands', 'cmd-k'],
        keys: ['Meta+K', 'Ctrl+K'],
        allowWhileTyping: true,
        run: () => { store.dispatch(INTENTS.PALETTE_OPEN); },
    });

    // Tabs.
    const TAB_LABELS: ReadonlyArray<readonly [
        'selection' | 'slices' | 'contour' | 'volume' | 'fieldlines' | 'vectors' | 'particles' | 'reductions',
        string,
        boolean,
    ]> = [
        ['selection',  'Selections',       true],
        ['slices',     'Field Slices',     true],
        ['contour',    'Contours',         true],
        ['volume',     'Volume Rendering', true],
        ['fieldlines', 'Field Lines',      true],
        ['vectors',    'Vectors',          false],
        ['particles',  'Particles',        false],
        ['reductions', 'Reductions',       true],
    ];
    for (const [key, label, enabled] of TAB_LABELS) {
        reg({
            id: `tab.open.${key}`,
            label: `Open ${label} tab`,
            category: 'Navigation',
            aliases: [key, label.toLowerCase()],
            ...(enabled ? {} : { enabled: false as const }),
            run: () => { store.dispatch(INTENTS.UI_TAB_OPEN, { which: key }); },
        });
    }

    // Primitives.
    reg({
        id: 'primitive.toggle.slices',
        label: 'Toggle field slices',
        category: 'Primitives',
        aliases: ['slice', 'xy', 'zx', 'zy'],
        run: () => {
            const anyOn = params.showFieldSlice || params.showFieldSliceZY || params.showFieldSliceZX;
            const next = !anyOn;
            store.dispatch(INTENTS.SET_SLICE_VISIBILITY, { axis: 'XY', visible: next });
            store.dispatch(INTENTS.SET_SLICE_VISIBILITY, { axis: 'ZY', visible: next });
            store.dispatch(INTENTS.SET_SLICE_VISIBILITY, { axis: 'ZX', visible: next });
        },
    });
    reg({
        id: 'primitive.toggle.volume',
        label: 'Toggle volume rendering',
        category: 'Primitives',
        aliases: ['render'],
        keys: ['V'],
        allowWhileTyping: true,
        run: () => { store.dispatch(INTENTS.TOGGLE_VOLUME); },
    });
    reg({
        id: 'primitive.toggle.fieldlines',
        label: 'Toggle field lines',
        category: 'Primitives',
        aliases: ['streamlines', 'trace'],
        run: () => {
            const visibleNow = params.showFieldLines;
            store.dispatch(INTENTS.SET_FIELDLINES_VISIBILITY, { visible: !visibleNow });
        },
    });
    reg({
        id: 'primitive.fieldlines.trace',
        label: 'Trace field line from selection',
        category: 'Primitives',
        aliases: ['streamline', 'fieldline'],
        keys: ['T'],
        allowWhileTyping: true,
        run: () => { store.dispatch(INTENTS.TRACE_FIELDLINE); },
    });
    reg({
        id: 'primitive.fieldlines.clear',
        label: 'Clear field lines',
        category: 'Primitives',
        keys: ['Shift+T'],
        allowWhileTyping: true,
        run: () => { store.dispatch(INTENTS.CLEAR_FIELDLINES); },
    });
    reg({
        id: 'primitive.contour.generate',
        label: 'Generate contour',
        category: 'Primitives',
        aliases: ['isosurface'],
        keys: ['C'],
        allowWhileTyping: true,
        run: () => { store.dispatch(INTENTS.GENERATE_CONTOUR); },
    });
    reg({
        id: 'primitive.contour.clear',
        label: 'Clear contours',
        category: 'Primitives',
        keys: ['Shift+C'],
        allowWhileTyping: true,
        run: () => { store.dispatch(INTENTS.CLEAR_CONTOURS); },
    });

    // Reductions.
    reg({
        id: 'reduction.add',
        label: 'Add reduction',
        category: 'Reductions',
        aliases: ['extract', 'profile', 'mean', 'integrate'],
        run: () => {
            store.dispatch(INTENTS.REDUCTION_CREATE, { focus: true });
            // Open the Reductions tab so the new entry is visible.
            store.dispatch(INTENTS.UI_TAB_OPEN, { which: 'reductions' });
        },
    });

    // Theme + view.
    reg({
        id: 'theme.cycle',
        label: 'Cycle theme',
        category: 'Theme',
        aliases: ['palette', 'dark', 'light'],
        keys: ["'"],
        allowWhileTyping: true,
        run: () => { store.dispatch(INTENTS.CYCLE_THEME); },
    });
    reg({
        id: 'view.cycleChrome',
        label: 'Cycle UI panel visibility',
        category: 'View',
        aliases: ['hide chrome', 'toggle ui'],
        keys: ['F'],
        allowWhileTyping: true,
        run: () => { store.dispatch(INTENTS.UI_PANEL_CYCLE); },
    });
    reg({
        id: 'view.toggleGridXZ',
        label: 'Toggle XZ grid',
        category: 'View',
        keys: ['G'],
        allowWhileTyping: true,
        run: () => { store.dispatch(INTENTS.SET_SHOW_GRID, { plane: 'XZ', value: !params.showGridXZ }); },
    });
    reg({
        id: 'view.toggleGridXY',
        label: 'Toggle XY grid',
        category: 'View',
        keys: ['Shift+G'],
        allowWhileTyping: true,
        run: () => { store.dispatch(INTENTS.SET_SHOW_GRID, { plane: 'XY', value: !params.showGridXY }); },
    });
    reg({
        id: 'view.toggleProjection',
        label: 'Toggle perspective/orthographic',
        category: 'View',
        aliases: ['projection', 'ortho', 'perspective', 'camera'],
        run: () => {
            const next = params.cameraProjection === 'orthographic' ? 'perspective' : 'orthographic';
            store.dispatch(INTENTS.CAMERA_SET_PROJECTION, { mode: next });
        },
    });
    reg({
        id: 'view.togglePerf',
        label: 'Toggle FPS / performance overlay',
        category: 'View',
        aliases: ['fps', 'performance', 'stats', 'frame rate', 'benchmark'],
        keys: ['Shift+P'],
        // Click the cluster button so its handler stays the single source of
        // truth for body.stats-shown + the button's pressed state.
        run: () => { document.getElementById('stats-toggle')?.click(); },
    });

    // Navigation mode: orbit / fly toggled by N; turntable is a presentation
    // mode reachable only here (or the console), not the N cycle or badge click.
    reg({
        id: 'camera.nav.cycle',
        label: 'Toggle navigation (orbit / fly)',
        category: 'Navigation',
        aliases: ['fly', 'orbit', 'camera mode', 'movement'],
        keys: ['N'],
        run: () => { store.dispatch(INTENTS.CAMERA_CYCLE_NAV_MODE); },
    });
    reg({
        id: 'camera.nav.orbit',
        label: 'Navigation: orbit',
        category: 'Navigation',
        aliases: ['camera mode', 'default'],
        run: () => { store.dispatch(INTENTS.CAMERA_SET_NAV_MODE, { mode: 'orbit' }); },
    });
    reg({
        id: 'camera.nav.turntable',
        label: 'Navigation: turntable (auto-spin)',
        category: 'Navigation',
        aliases: ['camera mode', 'spin', 'rotate', 'presentation'],
        run: () => { store.dispatch(INTENTS.CAMERA_SET_NAV_MODE, { mode: 'turntable' }); },
    });
    reg({
        id: 'camera.nav.fly',
        label: 'Navigation: fly (6DOF)',
        category: 'Navigation',
        aliases: ['camera mode', 'first person', 'free', 'wasd'],
        run: () => { store.dispatch(INTENTS.CAMERA_SET_NAV_MODE, { mode: 'fly' }); },
    });

    // Camera presets (XY / XZ / YZ / ISO + reset). The R key resets;
    // the other presets stay palette-only since the letter keys collide
    // with primitive toggles.
    reg({
        id: 'camera.preset.xy',
        label: 'Camera: look down +Z (XY plane)',
        category: 'View',
        aliases: ['top', 'down', 'plan view'],
        run: () => { store.dispatch(INTENTS.CAMERA_PRESET, { preset: 'XY' }); },
    });
    reg({
        id: 'camera.preset.xz',
        label: 'Camera: look down +Y (XZ plane)',
        category: 'View',
        aliases: ['front', 'side view'],
        run: () => { store.dispatch(INTENTS.CAMERA_PRESET, { preset: 'XZ' }); },
    });
    reg({
        id: 'camera.preset.yz',
        label: 'Camera: look down +X (YZ plane)',
        category: 'View',
        aliases: ['right', 'profile'],
        run: () => { store.dispatch(INTENTS.CAMERA_PRESET, { preset: 'YZ' }); },
    });
    reg({
        id: 'camera.preset.iso',
        label: 'Camera: isometric view',
        category: 'View',
        aliases: ['3d', 'perspective'],
        run: () => { store.dispatch(INTENTS.CAMERA_PRESET, { preset: 'ISO' }); },
    });
    reg({
        id: 'camera.preset.reset',
        label: 'Camera: reset view',
        category: 'View',
        aliases: ['home', 'default'],
        keys: ['R'],
        run: () => { store.dispatch(INTENTS.CAMERA_PRESET, { preset: 'RESET' }); },
    });

    // Timestep scrubber. Inert when availableSteps.length <= 1 (the
    // intent handler clamps, so the dispatch is a safe no-op).
    reg({
        id: 'timestep.prev',
        label: 'Previous timestep',
        category: 'Navigation',
        aliases: ['back', 'step backward'],
        keys: ['['],
        run: () => { store.dispatch(INTENTS.STEP_PREV); },
    });
    reg({
        id: 'timestep.next',
        label: 'Next timestep',
        category: 'Navigation',
        aliases: ['forward', 'step forward'],
        keys: [']'],
        run: () => { store.dispatch(INTENTS.STEP_NEXT); },
    });

    // Misc.
    reg({
        id: 'screenshot.save',
        label: 'Save PNG screenshot',
        category: 'Misc',
        aliases: ['save image', 'export png'],
        run: () => { store.dispatch(INTENTS.UI_SCREENSHOT_SAVE); },
    });
    reg({
        id: 'console.toggle',
        label: 'Toggle console',
        category: 'View',
        aliases: ['terminal', 'log'],
        run: () => {
            const w = windowRegistry.get('console');
            if (w) w.toggle();
        },
    });
    reg({
        id: 'help.open',
        label: 'Show help',
        category: 'View',
        aliases: ['shortcuts', 'documentation'],
        keys: ['?'],
        run: () => {
            const el = document.getElementById('help-panel');
            if (!el) return;
            if (isHelpOpen()) el.classList.remove('show');
            else el.classList.add('show');
        },
    });
    void viewer; // viewer is reserved for future commands (e.g. probe-at-cursor)

    // ─── dynamic command sync ──────────────────────────────────────────────

    const dynamicSelectionIds = new Set<string>();
    const dynamicReductionIds = new Set<string>();

    const syncSelectionCommands = (): void => {
        const live = new Set<string>(params.selectionOrder);
        // Remove commands for deleted selections.
        for (const id of Array.from(dynamicSelectionIds)) {
            if (!live.has(id)) {
                registry.unregister(`selection.focus.${id}`);
                dynamicSelectionIds.delete(id);
            }
        }
        // Register fresh ones.
        for (const id of params.selectionOrder) {
            if (dynamicSelectionIds.has(id)) continue;
            const sel = params.selections[id];
            if (!sel) continue;
            const off = registry.register({
                id: `selection.focus.${id}`,
                label: `Focus selection: ${sel.label}`,
                category: 'Selections',
                aliases: [sel.label, sel.kind, id],
                run: () => {
                    store.dispatch(INTENTS.SELECTION_FOCUS, { id });
                    store.dispatch(INTENTS.UI_TAB_OPEN, { which: 'selection' });
                },
            });
            registrations.push(off);
            dynamicSelectionIds.add(id);
        }
    };

    const syncReductionCommands = (): void => {
        const live = new Set<string>(params.reductionOrder);
        for (const id of Array.from(dynamicReductionIds)) {
            if (!live.has(id)) {
                registry.unregister(`reduction.focus.${id}`);
                dynamicReductionIds.delete(id);
            }
        }
        for (const id of params.reductionOrder) {
            if (dynamicReductionIds.has(id)) continue;
            const r = params.reductions[id];
            if (!r) continue;
            const off = registry.register({
                id: `reduction.focus.${id}`,
                label: `Focus reduction: ${r.label}`,
                category: 'Reductions',
                aliases: [r.label, r.field, r.op, id],
                run: () => {
                    store.dispatch(INTENTS.REDUCTION_FOCUS, { id });
                    store.dispatch(INTENTS.UI_TAB_OPEN, { which: 'reductions' });
                },
            });
            registrations.push(off);
            dynamicReductionIds.add(id);
        }
    };

    syncSelectionCommands();
    syncReductionCommands();

    const offSelOrder = store.subscribe('params.selectionOrder', syncSelectionCommands);
    const offRedOrder = store.subscribe('params.reductionOrder', syncReductionCommands);
    // Selection / reduction labels can be edited in-place — refresh on any
    // patch so the palette shows the live name.
    const offSelEdits = store.subscribe('params.selections', syncSelectionCommands);
    const offRedEdits = store.subscribe('params.reductions', syncReductionCommands);

    // ─── intent wiring ─────────────────────────────────────────────────────

    const offOpenIntent = store.onIntent(INTENTS.PALETTE_OPEN, () => openPalette());
    const offCloseIntent = store.onIntent(INTENTS.PALETTE_CLOSE, () => closePalette());

    // Hide on screenshot (chrome-hide flow). The screenshot intent fires
    // chrome:hide, but the palette is *not* part of chrome (it's a modal),
    // so close it explicitly too.
    const offChromeHide = store.onIntent(INTENTS.UI_CHROME_HIDE, () => closePalette());

    // Install global key listener (registry-driven).
    const offGlobal = registry.installGlobalKeyListener();

    // Re-render the palette list when commands appear/disappear while open.
    const offRegistry = registry.subscribe(() => { if (open) render(); });

    return () => {
        offOpenIntent();
        offCloseIntent();
        offChromeHide();
        offGlobal();
        offRegistry();
        offSelOrder();
        offRedOrder();
        offSelEdits();
        offRedEdits();
        for (const off of registrations) {
            try { off(); } catch { /* swallow */ }
        }
        registrations.length = 0;
        if (open) closePalette();
        try { dom.root.remove(); } catch { /* swallow */ }
    };
}

/** Test-only export of the registry singleton accessor. */
export { getShortcutRegistry as _registryForTests } from '@input/shortcutRegistry';
